import classnames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useApolloClient } from '@apollo/client'
import { CopyOutlined } from '@ant-design/icons'
import { Divider, Tooltip } from 'antd'
import imDfmFeedbackQuery from '@Admin/AdminQuotingPage/ImDfmSlidesPanel/imDfmFeedbackQuery'
import { imDfmFeedback_quoteModelRevision_dfmFeedback as DfmFeedbackType } from '@Admin/AdminQuotingPage/ImDfmSlidesPanel/__generated__/imDfmFeedback'

import Link from '~/core/Link'
import {
    createImDfm3dSlide,
    duplicateImDfm3dSlide,
} from '~/redux-modules/unsaved-im-dfm-3d-slides/actions'
import {
    getSlideToBeDuplicated,
    getUnsavedImDfm3dSlides,
} from '~/redux-modules/unsaved-im-dfm-3d-slides/selector'
import { UnsavedSlideStateType } from '~/redux-modules/unsaved-im-dfm-3d-slides/reducer'
import { DFM_NO_ACTION_REQUIRED } from '../../constants'

import styles from './styles.css'

const DISABLE_COPY_ICON_TOOLTIP =
    'Please save your current 3D slide to duplicate a 3D slide'

type ParamsType = {
    id: string
}

interface DuplicateSlideIconPropsType {
    dfmFeedbackId?: string | null
    idx: string
    isUnsaved?: boolean
}

const DuplicateSlideIcon = (props: DuplicateSlideIconPropsType) => {
    const { dfmFeedbackId, idx } = props

    const client = useApolloClient()
    const dispatch = useDispatch()
    const { id: qmrId = '' } = useParams<ParamsType>()

    const slideTobeDuplicated = useSelector((state: UnsavedSlideStateType) =>
        getSlideToBeDuplicated(state, qmrId, idx),
    )

    const unsavedImDfm3dSlides = useSelector((state: UnsavedSlideStateType) =>
        getUnsavedImDfm3dSlides(state, qmrId),
    )

    const hasUnsavedImDfmSlide = unsavedImDfm3dSlides?.length >= 1

    const handleDuplicateSlide = () => {
        const duplicateInfo = {
            qmrId,
            status: DFM_NO_ACTION_REQUIRED,
            sideIdToBeDuplicated: idx,
        }

        // Check if the 3D slide is in the redux store (unsaved slide)
        if (slideTobeDuplicated) {
            const duplicateSlideReduxInfo = {
                ...duplicateInfo,
                messageBody: slideTobeDuplicated?.messageBody,
                topic: slideTobeDuplicated?.topic,
            }

            return dispatch(duplicateImDfm3dSlide(duplicateSlideReduxInfo))
        }

        // If the slide is not in the redux store, check the cache
        // for the saved 3D DFM
        const cachedImDfmFeedbackData = client.readQuery({
            query: imDfmFeedbackQuery,
            variables: { qmrId },
        })

        const dfmFeedback =
            cachedImDfmFeedbackData.quoteModelRevision.dfmFeedback
        const dfmFeedbackToDuplicate = dfmFeedback.find(
            (dfm: DfmFeedbackType) => dfm.id === dfmFeedbackId,
        )

        const duplicateSlideApiInfo = {
            ...duplicateInfo,
            messageBody: dfmFeedbackToDuplicate?.message,
            topic: dfmFeedbackToDuplicate?.topic.label,
        }

        return dispatch(createImDfm3dSlide(duplicateSlideApiInfo))
    }

    const tooltipTitle = hasUnsavedImDfmSlide
        ? DISABLE_COPY_ICON_TOOLTIP
        : 'Copy'

    return (
        <>
            <Tooltip
                title={tooltipTitle}
                placement={hasUnsavedImDfmSlide ? 'bottom' : 'top'}
            >
                <Link
                    data-test-target="copy-icon"
                    name="copy-slide"
                    onClick={(evt: Event) => {
                        evt.stopPropagation()
                        evt.preventDefault()
                    }}
                    placement="im-dfm-slide"
                >
                    <CopyOutlined
                        className={classnames(
                            styles.copyIcon,
                            hasUnsavedImDfmSlide
                                ? styles.copyIconDisabled
                                : styles.copyIconActive,
                        )}
                        data-test-target="copy-icon"
                        onClick={() => {
                            if (!hasUnsavedImDfmSlide) {
                                handleDuplicateSlide()
                            }
                        }}
                    />
                </Link>
            </Tooltip>
            <Divider type="vertical" style={{ height: '22px' }} />
        </>
    )
}

export default DuplicateSlideIcon
